import * as React from "react";

import * as R from "ramda";

interface GenCheckboxesFilterProps extends ConnectedListFilterInterface {
  displayLikeFilterGroup?: boolean;
  title: string;
  filterKey: string;
  options: Array<string | {
    displayName: string;
    value: string | number | boolean;
  }>;
  dataType: "MDATA" | "REGULAR" | "BOOLEAN";
  dataCount?: "MDATA_MANY";
  filterColumn?: string;
}

export default function GenCheckboxesFilter(props: GenCheckboxesFilterProps) {
  const {
    displayLikeFilterGroup,
    filterKey,
    options,
    title,
    activeFilters={},
    addListFilter,
    removeListFilter,
    resetListPages,
    readListPage,
    dataType,
    dataCount,
    filterColumn,
  } = props;

  const [forceOpen, setForceOpen] = React.useState(false);

  const activeFilter = activeFilters[filterKey];
  const values = activeFilter ? getValueFromFilterWhereQuery(activeFilter) : undefined;

  const normalizedOptions = options.map((option) => {
     if (typeof option === "object") {
      return option;
     } else {
      return {
        displayName: option,
        value: option,
      };
     }
  });

  let classes = "gen-list-filter-2";

  if (displayLikeFilterGroup) {
    classes += " like-checkbox-filters";
  }

  const isOpen = !!values || forceOpen;

  return (
    <div className={classes}>
      <div
        className="gen-list-filter-title" onClick={() => {
          if (isOpen) {
            removeListFilter(filterKey);
            resetListPages();
            readListPage();
            setForceOpen(false);
          } else {
            setForceOpen(true);
          }
        }}
      >
        {displayLikeFilterGroup ? <h4>{title}</h4> : <h5>{title}</h5>}
        <div className="cursor-pointer"><span>{isOpen ? "All" : "Filter"}</span></div>
      </div>
      {isOpen && (
        <div className="gen-list-filter-options">
          {normalizedOptions.map((option) => {
            const key = `${title}-${option.displayName}`;

            const isChecked = !!values && values.includes(option.value);
            const onChange = () => {
              const newValues = isChecked ? values.filter((o) => {return o !== option.value;}) : (values || []).concat([option.value]);
              const newFilter = generateFilterWhereQueryFromValues(filterKey, newValues, dataType, dataCount, filterColumn);

              if (newFilter) {
                addListFilter(filterKey, newFilter);
              } else {
                removeListFilter(filterKey);
              }

              resetListPages();
              readListPage();
            };

            return(
              <div key={key} className="gen-list-filter-option">
                <label>
                  <input
                    id={key}
                    name={key}
                    type="checkbox"
                    checked={isChecked}
                    onChange={onChange}
                  />
                  {option.displayName}
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function generateFilterWhereQueryFromValues(filterKey: string, values: Array<unknown>, dataType: "MDATA" | "REGULAR" | "BOOLEAN", dataCount?: "MDATA_MANY", filterColumn?: string) {
  if (values && values.length > 0) {
    const whereArrays = values.map((value) => {
      switch (dataType) {
        case "MDATA":
          if (dataCount === "MDATA_MANY") {
            value = [value];
          }
          return ["@>", filterColumn || "mdata", {[filterKey]: value}] as Filter;
        case "REGULAR":
          return ["=", filterColumn || filterKey, value] as Filter;
        case "BOOLEAN":
          const opp = value ? "is_not_null" : "is_null";
          return [opp, filterColumn || filterKey] as Filter;
      }
    });

    if (whereArrays.length > 0) {
      return ["or", ...whereArrays] as Filter;
    } else {
      return whereArrays[0];
    }
  }

  return undefined;
}

function getValueFromFilterWhereQuery(filterWhereQuery: Filter): Array<string | number | boolean> {
  switch (filterWhereQuery[0]) {
    case "@>": {
      const val = R.values(R.last(filterWhereQuery) as Record<string, [string] | string>)[0];
      return Array.isArray(val) ? val : [val];
    }
    case "=":
      return [R.last(filterWhereQuery) as string];
    case "is_not_null":
      return [true];
    case "is_null":
      return [false];
    case "or":
    case "and":
      return (R.tail(filterWhereQuery) as Array<Filter>).flatMap((filter) => getValueFromFilterWhereQuery(filter));
    default:
      return [];
  }
}
